import { ToolIntegrationToolType } from '../store/types';

export interface IssueTypeAbbreviation {
    issueTypeId: string;
    abbreviation: string;
}

type ServerIssueTypeAbbreviation = {
    IssueTypeId: string;
    Abbreviation: string;
};

export const isIssueTypeVisibilitySupported = (toolType: ToolIntegrationToolType): boolean =>
    [ToolIntegrationToolType.Jira, ToolIntegrationToolType.Tfs, ToolIntegrationToolType.Vsts].includes(toolType);

export const getIssueTypeLabel = (toolType: ToolIntegrationToolType): string => {
    switch (toolType) {
        case ToolIntegrationToolType.Jira:
            return 'Jira Issue Type';
        case ToolIntegrationToolType.Vsts:
            return 'Azure DevOps Issue Type';
        case ToolIntegrationToolType.Tfs:
            return 'TFS Issue Type';
        default:
            return 'Issue Type';
    }
};

const defaultAbbreviations: Record<string, string> = {
    story: 'S',
    bug: 'B',
    task: 'T',
    epic: 'E',
    'sub-task': 'ST',
    subtask: 'ST',
    'user story': 'US',
    feature: 'F',
    improvement: 'I',
    spike: 'Sp',
    'change request': 'CR',
    'technical debt': 'TD',
    incident: 'IN',
};

const normalize = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, ' ');

const getIssueTypeId = (override: IssueTypeAbbreviation | ServerIssueTypeAbbreviation): string =>
    'IssueTypeId' in override ? override.IssueTypeId : override.issueTypeId;

const getAbbreviation = (override: IssueTypeAbbreviation | ServerIssueTypeAbbreviation): string =>
    'Abbreviation' in override ? override.Abbreviation : override.abbreviation;

export const resolveIssueTypeAbbreviation = (
    integrationToolType: ToolIntegrationToolType,
    linkedItemTypeId: string | null,
    linkedItemTypeName: string | null,
    issueTypeAbbreviations: (IssueTypeAbbreviation | ServerIssueTypeAbbreviation)[] = []
): string | null => {
    if (!isIssueTypeVisibilitySupported(integrationToolType) || linkedItemTypeName == null || linkedItemTypeName.trim() === '') {
        return null;
    }

    const matchingOverride = issueTypeAbbreviations
        .filter((x) => getIssueTypeId(x) === linkedItemTypeId)
        .map(getAbbreviation)
        .find((x) => x != null && x.trim() !== '');

    if (integrationToolType === ToolIntegrationToolType.Jira && matchingOverride) {
        return matchingOverride.trim();
    }

    const normalizedName = normalize(linkedItemTypeName);
    if (defaultAbbreviations[normalizedName]) {
        return defaultAbbreviations[normalizedName];
    }

    const words = normalizedName.split(/[\s_-]+/).filter((x) => x !== '');
    if (words.length > 1) {
        return words.map((x) => x[0].toUpperCase()).join('');
    }

    if (normalizedName.length === 1) {
        return normalizedName.toUpperCase();
    }

    return normalizedName.substring(0, 2).toUpperCase();
};

export const parseIssueTypeAbbreviations = (json: string | null): IssueTypeAbbreviation[] => {
    if (json == null || json.trim() === '') {
        return [];
    }

    try {
        const parsed = JSON.parse(json) as Record<string, string>;
        return Object.keys(parsed).map((issueTypeId) => ({
            issueTypeId,
            abbreviation: parsed[issueTypeId],
        }));
    } catch {
        return [];
    }
};
